import type { FC } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { LoaderCircle, Popup } from '@skytech/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import type { SendSMS } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

import type { CustomNumberStateType, NumberRecordType, PhoneBookType } from '../types'

import { CustomPhoneNumberForm } from './CustomPhoneNumberForm'
import { ComposeSMSForm } from './Form'
import { GroupForm } from './GroupForm'
import { PhoneBooks } from './PhoneBooks'
import { PhoneNumbers } from './PhoneNumbers'
import { RecipientsList } from './RecipientsList'
import { SelectedPhoneNumbers } from './SelectedPhoneNumbers'
import { formattedData } from './util'

import queries from '../../queries'

export const ComposeSMSModal: FC<{
  numbers?: { id: number; name: string; countryId: number; number: string }[]
  onClose: () => void
}> = ({ numbers, onClose }) => {
  const slideRef = useRef<HTMLDivElement>(null)
  const pageRef = useRef<HTMLDivElement>(null)
  const translate = useTranslation()

  const [selectedPhoneBook, setSelectedPhoneBook] = useState<PhoneBookType>()
  const [selectedNumbers, setSelectedNumbers] = useState<Map<string, NumberRecordType>>(new Map())
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [showCustomNumberModal, setShowCustomNumberModal] = useState<CustomNumberStateType>()

  const queryClient = useQueryClient()

  const activeNumbersQR = useQuery(
    queries.adminActiveNumbersList(),
    adminResources.getActiveNumbersList
  )

  const sendSMSMutation = useMutation(adminResources.sendSMS, {
    onSuccess() {
      queryClient.invalidateQueries(queries.adminSMSLogsList())
      onClose()
    },
  })

  const phoneBooks = useMemo(() => {
    if (!activeNumbersQR.data) return []

    return formattedData([...activeNumbersQR.data])
  }, [activeNumbersQR.data])

  useEffect(() => {
    if (!selectedPhoneBook) {
      setSelectedPhoneBook(phoneBooks[0])
    }
  }, [phoneBooks, selectedPhoneBook])

  useEffect(() => {
    if (numbers && numbers.length > 0 && selectedNumbers.size === 0) {
      const state = new Map(selectedNumbers)

      numbers.forEach(item => {
        const fullNumber = `${item.countryId}${item.number}`
        state.set(fullNumber, item)
      })

      setSelectedNumbers(state)

      scrollToNextPage(1)
    }
  }, [numbers, selectedNumbers])

  const scrollToNextSlide = (index: number) => {
    const containerElement = slideRef.current
    if (containerElement) {
      const elements = containerElement.getElementsByClassName('snap-start')

      if (elements.length > 0) {
        const nextElement = elements[index]
        nextElement.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  const scrollToNextPage = (index: number) => {
    const containerElement = pageRef.current
    if (containerElement) {
      const elements = containerElement.getElementsByClassName('page')

      if (elements.length > 0) {
        const nextElement = elements[index]
        nextElement.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  const onSelectNumberHandler = (numbers: Map<string, NumberRecordType>) => {
    const state = new Map(selectedNumbers)

    numbers.forEach((item, fullNumber) => {
      if (state.has(fullNumber)) {
        state.delete(fullNumber)
      } else {
        state.set(fullNumber, item)
      }
    })

    setSelectedNumbers(state)
  }

  const onSendHandler = (values: Omit<SendSMS, 'receivers'>) => {
    // We spread the map here because datatype Map does not have a .map function like Arrays
    const receivers = [...selectedNumbers].map(([, item]) => {
      const { name, countryId, number } = item
      return { name, countryId, number }
    })

    const payload: SendSMS = {
      ...values,
      receivers,
    }

    sendSMSMutation.mutate({ payload })
  }

  const findSelectedPhoneBook = (): PhoneBookType => {
    const group = phoneBooks.find(item => item.type === selectedPhoneBook?.type)!

    if (!group.children) {
      return group
    } else {
      return group.children.get(selectedPhoneBook!.id as number)!
    }
  }

  if (!selectedPhoneBook) {
    return (
      <div
        className="flex h-[30rem] items-center justify-center lg:h-[35rem]"
        style={{ overflow: 'hidden' }}
        ref={pageRef}
      >
        <LoaderCircle size="regular" />
      </div>
    )
  }

  const isLoading =
    sendSMSMutation.isLoading || activeNumbersQR.isLoading || activeNumbersQR.isRefetching

  return (
    <>
      {isLoading && (
        <LoaderCircle
          size="regular"
          className="absolute z-50 flex h-full w-full translate-x-[-1.70rem] translate-y-[-1.25rem] items-center justify-center rounded-[1rem] bg-[#0f0f0f33]"
        />
      )}
      <div
        className="relative flex h-[30rem] min-h-[60px] w-full snap-x snap-mandatory overflow-x-hidden lg:h-[35rem]"
        ref={pageRef}
      >
        {/* //Page 1 */}
        {!numbers && (
          <div className="page flex h-full w-full flex-shrink-0 snap-start flex-col">
            <div className="mb-5 flex w-full items-center">
              <h2 className="mb-0">
                <Translation id="Select phone numbers" />
              </h2>
            </div>
            {/* //Container */}
            <div
              className="flex h-full snap-x snap-mandatory overflow-x-hidden overflow-y-hidden lg:overflow-x-hidden"
              ref={slideRef}
            >
              {/* //Slide 1 */}
              <PhoneBooks
                scrollToNextSlide={scrollToNextSlide}
                items={phoneBooks}
                selectedItem={findSelectedPhoneBook()}
                onClick={setSelectedPhoneBook}
              />
              {/* //Slide 2 */}
              <PhoneNumbers
                scrollToNextSlide={scrollToNextSlide}
                selectedPhoneBook={findSelectedPhoneBook()}
                selectedNumbers={selectedNumbers}
                onSelectNumberHandler={onSelectNumberHandler}
                onCustomNumber={setShowCustomNumberModal}
              />
              {/* //Slide 3 */}
              <SelectedPhoneNumbers
                scrollToNextPage={scrollToNextPage}
                scrollToNextSlide={scrollToNextSlide}
                selectedNumbers={selectedNumbers}
                onSelectNumberHandler={onSelectNumberHandler}
                openGroupModal={() => setShowGroupModal(true)}
              />
            </div>
          </div>
        )}
        {/* //Page 2 */}
        <div className="page flex w-full flex-shrink-0 snap-start flex-col">
          <div className="mb-5 flex w-full items-center">
            <h2 className="mb-0">
              <Translation id="Compose message" />
            </h2>
          </div>
          <div className="flex h-full w-full flex-col gap-1 overflow-hidden lg:flex-row lg:gap-3">
            {/* List of Contacts */}
            <RecipientsList selectedNumbers={selectedNumbers} />
            {/* Compose SMS */}
            <ComposeSMSForm
              scrollToNextPage={scrollToNextPage}
              onSend={onSendHandler}
              isLoading={sendSMSMutation.isLoading}
              canGoBack={!numbers}
            />
          </div>
        </div>
        <Popup
          ariaLabel={translate('Existing groups')}
          isOpen={showGroupModal}
          variant="narrow"
          onClose={() => setShowGroupModal(false)}
        >
          <GroupForm
            onClose={() => setShowGroupModal(false)}
            isOpen={showGroupModal}
            selectedNumbers={selectedNumbers}
          />
        </Popup>
        <Popup
          ariaLabel={translate('Custom phone number')}
          isOpen={Boolean(showCustomNumberModal)}
          variant="narrow"
          onClose={() => setShowCustomNumberModal(undefined)}
        >
          <CustomPhoneNumberForm
            onClose={() => setShowCustomNumberModal(undefined)}
            selectedPhoneBook={findSelectedPhoneBook()}
            initialData={showCustomNumberModal}
          />
        </Popup>
      </div>
    </>
  )
}
